import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FieldValues, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { PulseLoader } from 'react-spinners';
import { MdSearch } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { PiLightningDuotone, PiLightningSlashDuotone } from "react-icons/pi";
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { getUser } from '../../../../api/client';
import {
  upsertPosition, selectUpsertPositionStatus, selectPositionEditing,
  resetSecurities, fetchSecurities, selectSecurities, setSecuritySelected, selectSecuritySelected, selectFetchSecurityStatus,
  selectLastPrice,
  fetchLastPrice,
  resetLastPrice,
  addDynamicWeightPriceRange,
  removeDynamicWeight
} from './edit-position-slice';
import { instanceToPlain } from 'class-transformer';
import { setSelectedPosition } from '../portfolio-allocation/collapsible-position-slice';
import { recalculatePortfolio } from '../../portfolios-slice';
import { selectAccessToken } from '../../../authentication/authentication-slice';
import { isFund } from '../../../../app/utils';
import EditDynamicWeight from './edit-dynamic-weight';
import { Tooltip } from 'react-tooltip';
import { FaQuestionCircle } from 'react-icons/fa';

interface EditPositionDefaultProps {
  /**
   * The list of asset types to be used when retrieving the security list
   */
  assetTypeIds: string[],
  loadAllSecurities: boolean
}

/**
 * Form to add and edit a simple position, which helds some quantity of a security and no custom fields are necessary to detail the security.
 */
const EditPositionDefault: React.FC<EditPositionDefaultProps> = (props: EditPositionDefaultProps) => {
  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const accessToken = useAppSelector(selectAccessToken);
  const positionEditing = useAppSelector(selectPositionEditing);
  const securities = useAppSelector(selectSecurities);
  const securitySelected = useAppSelector(selectSecuritySelected);
  const fetchSecuritiesStatus = useAppSelector(selectFetchSecurityStatus);
  const upsertPositionStatus = useAppSelector(selectUpsertPositionStatus);
  const lastPrice = useAppSelector(selectLastPrice);
  const isNewPosition = positionEditing?.id ? false : true;
  const isSecuritySelected = securitySelected?.id ? true : false;
  const priceStep = isFund(securitySelected?.assetType) ? 1e-7 : 0.01;
  const hasDynamicWeight = positionEditing?.dynamicWeight !== undefined && positionEditing?.dynamicWeight !== null && positionEditing?.dynamicWeight.weightPriceRanges.length > 0;

  const [symbolFilter, setSymbolFilter] = useState(positionEditing?.security?.symbol ?? '');

  // React useEffect: applies after rendering
  useEffect(() => {
    if (props.loadAllSecurities && fetchSecuritiesStatus === 'idle') {
      const execute = async () => {
        if (accessToken) {
          dispatch(fetchSecurities({ accessToken, assetTypeIds: props.assetTypeIds, symbolFilter: '' }));
        }
      }
      execute();
    }
  });

  // React useEffect: applies after rendering. Reset symbol filter when assetTypeIds change.
  useEffect(() => {
    setSymbolFilter(positionEditing?.security?.symbol ?? '');
    dispatch(setSecuritySelected({ security: Object(instanceToPlain(positionEditing?.security)) }));
    if (accessToken && positionEditing?.security?.id) {
      dispatch(fetchLastPrice({ accessToken, securityId: positionEditing?.security?.id }))
    }
  }, [props.assetTypeIds, positionEditing?.security?.symbol]);

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    values: {
      securitySymbol: symbolFilter,
      quantity: positionEditing?.quantity,
      weight: positionEditing?.weight
    }
  });

  const watchQuantity = watch('quantity');

  // React useEffect: applies after rendering. If fetched securities return only 1 security, set it as the selected one.
  useEffect(() => {
    const filteredSecurities = securities.filter(security => security.symbol.includes(symbolFilter));
    if (filteredSecurities.length === 1) {
      dispatch(setSecuritySelected({ security: Object(instanceToPlain(filteredSecurities[0])) }));
    }
  }, [dispatch, securities, symbolFilter]);

  function securitySymbolChangeHandler(e: React.FormEvent<HTMLInputElement>): void {
    e.currentTarget.value = e.currentTarget.value.toUpperCase();
    const filter = e.currentTarget.value;
    if (!props.loadAllSecurities) {
      if (filter.length < 3) {
        dispatch(resetSecurities());
      } else if (filter.length === 3) {
        const execute = (async () => {
          const accessToken = await getAccessTokenSilently();
          dispatch(fetchSecurities({ accessToken, assetTypeIds: props.assetTypeIds, symbolFilter: filter }));
        })
        execute();
      }
    }
    const filteredSecurities = securities.filter(security => security.symbol.includes(filter));
    if (filteredSecurities.length === 1) {
      if (accessToken && filteredSecurities[0] && filteredSecurities[0].id !== undefined) {
        dispatch(setSecuritySelected({ security: Object(instanceToPlain(filteredSecurities[0])) }));
        dispatch(fetchLastPrice({ accessToken, securityId: filteredSecurities[0].id }));
      }
    } else {
      setSymbolFilter(filter);
    }
  }

  const securitySelectedClickHandler = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    setSymbolFilter(e.currentTarget.innerText);
    const security = securities.find(s => s.symbol === e.currentTarget.innerText);
    if (accessToken && security && security.id !== undefined) {
      dispatch(setSecuritySelected({ security: Object(instanceToPlain(security)) }));
      dispatch(fetchLastPrice({ accessToken, securityId: security.id }));
    }
    e.currentTarget?.blur();
  }

  const securitySelectedEnterHandler = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Enter') {
      setSymbolFilter(e.currentTarget.innerText);
      const security = securities.find(s => s.symbol === e.currentTarget.innerText);
      if (accessToken && security && security.id !== undefined) {
        dispatch(setSecuritySelected({ security: Object(instanceToPlain(security)) }));
        dispatch(fetchLastPrice({ accessToken, securityId: security.id }));
      }
      e.currentTarget?.blur();
    } else if (e.key === 'ArrowUp') {
      const element = document.activeElement?.previousElementSibling as HTMLElement;
      element?.focus();
    } else if (e.key === 'ArrowDown') {
      const element = document.activeElement?.nextElementSibling as HTMLElement;
      element?.focus();
    }
  }

  const removeSecurityHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setSymbolFilter('');
    dispatch(resetSecurities());
    dispatch(resetLastPrice());
  }

  const removeSecurityEnterHandler = (e: React.KeyboardEvent<SVGAElement>) => {
    if (e.key === 'Enter') {
      setSymbolFilter('');
      dispatch(resetSecurities());
      dispatch(resetLastPrice());
      e.currentTarget?.parentElement?.parentElement?.parentElement?.focus();
    }
  }

  const useOnSubmit = (data: FieldValues) => {
    if (!isNewPosition || securitySelected) {
      const execute = async () => {
        const accessToken = await getAccessTokenSilently();
        const user = await getUser(accessToken);
        if (user && positionEditing) {
          positionEditing.security = positionEditing.security ?? securitySelected;
          positionEditing.quantity = data['quantity'];
          positionEditing.weight = Number.parseInt(data['weight']);
          positionEditing.price = lastPrice?.price ?? positionEditing.price;
          dispatch(upsertPosition({ accessToken, user, position: positionEditing }));
          dispatch(setSelectedPosition(undefined));
          dispatch(recalculatePortfolio({ portfolioId: positionEditing.portfolioId }));

        }
      }
      execute();
    }
  };

  const addDynamicWeightHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    dispatch(addDynamicWeightPriceRange());
  }

  const removeDynamicWeightHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    dispatch(removeDynamicWeight());
  }

  return (
    <div className='flex flex-col w-full z-40'>
      <form onSubmit={handleSubmit(useOnSubmit)} className='w-full flex flex-col text-slate-700'>
        <div className='group flex flex-col mb-4 z-50'>
          <label className='ml-1'>Ativo</label>
          <div className='group flex w-full items-center pl-1 pr-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 bg-white has-[:disabled]:bg-gray-300 has-[:disabled]:border-gray-400 has-[:disabled]:text-gray-400'>
            {!isSecuritySelected &&
              <div className='flex flex-row w-full'>
                <MdSearch size={22} className='ml-1 text-neutral-400 group-has-[:disabled]:hidden' />
                <input disabled={!isNewPosition} type='text' autoComplete='off' className='w-full outline-none ml-1'
                  {...register("securitySymbol", { required: false })}
                  onInput={securitySymbolChangeHandler}
                />
              </div>
            }
            {isSecuritySelected && isNewPosition &&
              <div onClick={removeSecurityHandler} className='group/security-selected select-none cursor-pointer flex flex-row ml-1 rounded-lg pl-3 pr-1 bg-blue-900 text-white shadow-sm shadow-neutral-400 items-center has-[:hover]:bg-red-200 has-[:focus]:bg-red-200 has-[:hover]:ring-2 has-[:focus]:ring-2 has-[:hover]:ring-red-400 has-[:focus]:ring-red-400 transition-color delay-100 duration-75'>
                <div className='group-has-[:hover]/security-selected:text-red-600 group-has-[:focus]/security-selected:text-red-600 transition-color delay-100 duration-75'>{securitySelected?.symbol}</div>
                <div className='flex flex-row items-center'>
                  <div className='ml-2 h-3 border-l w-1 group-has-[:hover]/security-selected:border-red-600 group-has-[:focus]/security-selected:border-red-600 transition-color delay-100 duration-75'></div>
                  <IoClose onKeyDown={removeSecurityEnterHandler} tabIndex={0} size={18} className='outline-0 group-has-[:hover]/security-selected:text-red-600 group-has-[:focus]/security-selected:text-red-600 transition-color delay-100 duration-75' />
                </div>
              </div>
            }
            {isSecuritySelected && !isNewPosition &&
              <button disabled={true} className='px-2 text-gray-500'>{securitySelected?.symbol}</button>
            }
          </div>
          {!isSecuritySelected &&
            <div className='relative w-full'>
              <div className={`absolute top-0 left-0 right-0 p-1 overflow-hidden`}>
                <div className={`${symbolFilter.length > 0 && symbolFilter.length < 3 && !props.loadAllSecurities ? 'group-has-[:focus]:h-20 group-has-[:focus]:border group-has-[:focus]:ring-1 group-has-[:focus]:p-1' : 'border-0 ring-0'} h-0 overflow-hidden bg-white rounded-md ring-black border-neutral-400 font-semibold text-gray-400 shadow-lg shadow-neutral-400/40`}>
                  Pelo menos 3 letras na busca
                </div>
              </div>
              <div className={`absolute top-0 left-0 right-0 p-1 overflow-hidden`}>
                <ul className={`${securities.length > 0 ? 'group-has-[:focus]:h-36 group-has-[:focus]:border group-has-[:focus]:ring-1' : 'border-0 ring-0'} h-0 overflow-y-auto flex flex-col transition-all delay-0 duration-200 bg-white rounded-md ring-black border-neutral-400 shadow-lg shadow-neutral-400/40`}>
                  {securities.filter(security => security.symbol.includes(symbolFilter)).map(security =>
                    <li tabIndex={0} onKeyDown={securitySelectedEnterHandler} onClick={securitySelectedClickHandler} key={security.id} className='cursor-pointer rounded-md p-1 hover:bg-sky-100 focus:bg-sky-100 outline-none'>
                      {security.symbol}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          }
        </div>

        <div className={`flex flex-col focus:outline-0`}>
          <label className='ml-1'>Quantidade</label>
          <input disabled={isNewPosition && securitySelected === undefined} type='number' step={priceStep} className='px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 disabled:bg-gray-300 disabled:text-gray-500'
            {...register("quantity", {
              required: true
            })}
          />
          <ErrorMessage errors={errors} name="quantity" render={({ message }) => <div className='text-red-500 font-semibold'>{message}</div>} />
        </div>

        {lastPrice !== undefined && watchQuantity !== undefined && securitySelected !== undefined &&
          <div className='flex flex-col justify-center items-center rounded-md border p-2 border-neutral-300 bg-neutral-400/20 mt-4'>
            <div className='flex justify-center items-center'>
              <label className='text-xs mr-1'>Valor da posição:</label>
              <label>R$ {(lastPrice.price * watchQuantity.valueOf())?.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</label>
            </div>
            <div className='flex justify-center items-center'>
              <label className='text-xs mr-1'>Preço:</label>
              <label>R$ {lastPrice?.price?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
              <label className='text-xs ml-1'>({lastPrice?.priceDate?.toLocaleDateString([], {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              }).replace(', 00:00', '')})</label>
            </div>
          </div>
        }

        <div className='flex flex-col w-full mt-4 mb-4 z-50'>
          <div className='w-full flex flex-row items-center mb-1 lg:mb-2'>
            <label className='ml-1'>Peso</label>
            <div onClick={hasDynamicWeight ? removeDynamicWeightHandler : addDynamicWeightHandler}
              className={`${hasDynamicWeight ? 'w-9' : 'w-24 lg:w-28'} h-7 transition-all duration-300 cursor-pointer flex flex-row items-center px-2 py-1 text-yellow-300 rounded-full ml-2 bg-slate-800 hover:opacity-85 hover:shadow-lg hover:border-slate-600 hover:border-1`}>
              {hasDynamicWeight ? <PiLightningSlashDuotone size={18} className='' /> : <PiLightningDuotone size={20} className='min-w-fit' />}
              <label className={`${hasDynamicWeight ? '' : 'ml-1'} cursor-pointer`}>{hasDynamicWeight ? '' : 'Dinâmico'}</label>
            </div>
            <FaQuestionCircle data-tooltip-id='tooltipDynamicWeight' className='self-start ml-2' size={20} />
          </div>
          <Tooltip place='top' id='tooltipDynamicWeight' >
            <div className='flex flex-col items-center justify-center text-xs font-semibold text-neutral-300'>
              <label>O peso determina qual a proporção % ideal</label>
              <label>que esta posição terá no seu portfolio.<br /><br /></label>
              <label>Posições e subcategorias dentro da</label>
              <label>mesma categoria disputam entre si</label>
              <label>pela proporção que elas deterão.<br /><br /></label>
              <label>O peso dinâmico utiliza as faixas de preço</label>
              <label>informadas por você e determina o peso</label>
              <label>a ser utilizado, baseado em qual faixa o</label>
              <label>preço atual do ativo se encaixa.</label>
            </div>
          </Tooltip>
          {
            !hasDynamicWeight &&
            <div>
              <div className='w-full flex flex-row'>
                <input disabled={isNewPosition && securitySelected === undefined} type="number" placeholder="ex: 10"
                  className='w-full px-3 py-1 rounded-xl mt-1 border border-neutral-400 outline-slate-800 disabled:bg-gray-300 disabled:text-gray-500'
                  {...register("weight", { required: true, min: { value: 0, message: 'O peso não pode ser negativo' } })}
                />
              </div>
              <ErrorMessage errors={errors} name="weight" render={({ message }) => <div className='text-red-500 font-semibold'>{message}</div>} />
            </div>
          }
          {
            hasDynamicWeight &&
            <EditDynamicWeight />
          }
        </div>


        <div className='flex justify-center items-center mt-4'>
          <div hidden={upsertPositionStatus !== 'pending'} className='justify-self-center'>
            <PulseLoader size={8} color='currentColor' className='px-4 py-2 text-slate-200 rounded-full bg-slate-800 disabled:cursor-default disabled:bg-gray-600 hover:enabled:opacity-85 hover:enabled:shadow-lg hover:enabled:border-slate-600 hover:enabled:border-1' />
          </div>
          <input hidden={upsertPositionStatus === 'pending'} disabled={isNewPosition && securitySelected === undefined} type="submit" value="Enviar" className='cursor-pointer px-4 py-2 text-slate-200 rounded-full bg-slate-800 disabled:cursor-default disabled:bg-gray-400 hover:enabled:opacity-85 hover:enabled:shadow-lg hover:enabled:border-slate-600 hover:enabled:border-1' />
        </div>
      </form >
    </div>
  );
}

export default EditPositionDefault;